import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Header from '../../components/Header/Header';
import BottomNavigation from '../../components/BottomNavigation/BottomNavigation';
import { FaDatabase, FaChartLine, FaEthereum } from 'react-icons/fa';
import { SiTether } from 'react-icons/si';
import AssetIcon from '../../components/AssetIcon';
import Toast from '../../components/Toast';
import { formatCurrency, formatAsset } from '../../utils/format';
import './Serve.css';
import { useUser } from '../../context/UserContext';
import { userService } from '../../services/userService';
import { orderService } from '../../services/orderService';
import { miningAssetService } from '../../services/miningAssetService';
import { assemblyLineService } from '../../services/assemblyLineService';
import { withdrawService } from '../../services/withdrawService';

const Serve = ({ onTabChange }) => {
  const { t } = useTranslation();
  const { walletAddress } = useUser();
  const [stats, setStats] = useState([]);
  const [userStats, setUserStats] = useState({
    usdtBalance: 0,
    ethBalance: 0,
    withdrawableEarnings: 0,
  });
  const [miningAssets, setMiningAssets] = useState([]);
  const [purchaseRecords, setPurchaseRecords] = useState([]);
  const [assemblyLineRecords, setAssemblyLineRecords] = useState([]);
  const [myMiningAssets, setMyMiningAssets] = useState([]); // 我的挖矿资产
  const [loading, setLoading] = useState(false);

  const platforms = [
    { id: 'binance', name: 'Binance', icon: '💎' },
    { id: 'lbank', name: 'LBank', icon: '🅱️' },
    { id: 'kucoin', name: 'KuCoin', icon: '🟢' },
    { id: 'okex', name: 'OKEx', icon: '🔵' },
    { id: 'bitfinex', name: 'Bitfinex', icon: '🍃' },
    { id: 'wazirx', name: 'WazirX', icon: '🔷' },
    { id: 'gate', name: 'Gate.io', icon: '⭕' },
    { id: 'upbit', name: 'Upbit', icon: '⬆️' },
    { id: 'coinbase', name: 'Coinbase', icon: '🔵' },
  ];

  // 初始化 stats
  useEffect(() => {
    setStats([
      { id: 'total', label: t('home.totalEarnings'), value: '0', icon: <FaDatabase /> },
      { id: 'today', label: t('home.todayEarnings'), value: '0', icon: <FaChartLine /> },
    ]);
  }, [t]);

  useEffect(() => {
    if (walletAddress) {
      loadUserData();
    }
    loadMiningAssets();
  }, [walletAddress]);

  // 当挖矿资产列表或流水线记录更新时，重新计算我的挖矿资产
  useEffect(() => {
    if (assemblyLineRecords.length > 0 && miningAssets.length > 0) {
      calculateMyMiningAssets(assemblyLineRecords);
    }
  }, [assemblyLineRecords, miningAssets]);

  const loadUserData = async () => {
    if (!walletAddress) return;
    
    try {
      setLoading(true);
      
      // 加载用户资产统计
      const statsData = await userService.getUserStats(walletAddress);
      if (statsData) {
        setUserStats({
          usdtBalance: statsData.usdtBalance || 0,
          ethBalance: statsData.ethBalance || 0,
          withdrawableEarnings: statsData.withdrawableEarnings || 0,
        });
        
        setStats([
          { id: 'total', label: t('home.totalEarnings'), value: (statsData.totalRevenue || 0).toString(), icon: <FaDatabase /> },
          { id: 'today', label: t('home.todayEarnings'), value: '0', icon: <FaChartLine /> },
        ]);
      }
      
      // 加载购买记录
      const orders = await orderService.getUserOrders(walletAddress, 1, 10);
      if (orders && orders.records) {
        setPurchaseRecords(orders.records);
      }
      
      // 加载流水线记录
      const assemblyLines = await assemblyLineService.getUserAssemblyLines(walletAddress);
      if (assemblyLines) {
        setAssemblyLineRecords(assemblyLines);
      }
    } catch (error) {
      console.error('加载用户数据失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMiningAssets = async () => {
    try {
      const assets = await miningAssetService.getTodayAssets();
      if (assets) {
        setMiningAssets(assets);
      } else {
        // 默认数据（图标由 AssetIcon 组件处理）
        setMiningAssets([
          { id: 'aster', name: 'ASTER', price: '1.13' },
          { id: 'atom', name: 'ATOM', price: '3.02' },
          { id: 'santos', name: 'SANTOS', price: '1.36' },
          { id: 'xlm', name: 'XLM', price: '0.3' },
        ]);
      }
    } catch (error) {
      console.error('加载挖矿资产失败:', error);
      // 使用默认数据（图标由 AssetIcon 组件处理）
      setMiningAssets([
        { id: 'aster', name: 'ASTER', price: '1.13' },
        { id: 'atom', name: 'ATOM', price: '3.02' },
        { id: 'santos', name: 'SANTOS', price: '1.36' },
        { id: 'xlm', name: 'XLM', price: '0.3' },
      ]);
    }
  };

  // 计算我的挖矿资产（从流水线记录中统计）
  const calculateMyMiningAssets = (assemblyLines) => {
    if (!assemblyLines || assemblyLines.length === 0) {
      setMyMiningAssets([]);
      return;
    }

    // 统计每种挖矿资产的数量（统一统计逻辑，和后端保持一致）
    // 统计所有相关记录，包括增加和扣除
    const assetMap = {};
    assemblyLines.forEach(line => {
      // 统计所有挖矿资产类型（不包括USDT和ETH）
      if (line.assetType && line.assetType !== 'USDT' && line.assetType !== 'ETH') {
        const assetName = line.assetType;
        if (!assetMap[assetName]) {
          assetMap[assetName] = {
            name: assetName,
            quantity: 0,
          };
        }
        // 累加所有数量（正数表示增加，负数表示扣除）
        assetMap[assetName].quantity += parseFloat(line.quantity || 0);
      }
    });
    
    // 确保余额不为负数
    Object.keys(assetMap).forEach(key => {
      if (assetMap[key].quantity < 0) {
        assetMap[key].quantity = 0;
      }
    });

    // 获取资产价格并计算总价值
    const myAssets = Object.values(assetMap).map(asset => {
      // 从今日资产列表中查找价格
      const todayAsset = miningAssets.find(a => a.name === asset.name);
      const price = todayAsset ? parseFloat(todayAsset.price || 0) : 0;
      const totalValue = asset.quantity * price;
      
      return {
        name: asset.name,
        quantity: asset.quantity.toFixed(8),
        price: price.toFixed(2),
        totalValue: totalValue.toFixed(2),
      };
    });

    setMyMiningAssets(myAssets);
  };

  const handleWithdraw = async () => {
    if (!walletAddress) {
      Toast.warning('请先连接钱包');
      return;
    }

    if (userStats.withdrawableEarnings <= 0) {
      Toast.warning('可提取收益不足');
      return;
    }

    const amountStr = prompt(`请输入提现金额（最大 ${formatCurrency(userStats.withdrawableEarnings)}）:`, formatCurrency(userStats.withdrawableEarnings));
    if (!amountStr) {
      return; // 用户取消
    }
    
    const amount = parseFloat(amountStr);
    
    if (isNaN(amount) || amount <= 0) {
      Toast.error('请输入有效的提现金额');
      return;
    }

    if (amount > userStats.withdrawableEarnings) {
      Toast.error('提现金额不能超过可提取收益');
      return;
    }

    try {
      setLoading(true);
      const result = await withdrawService.createWithdraw(walletAddress, amount, 'USDT');
      Toast.success('提现申请成功！');
      // 刷新数据
      loadUserData();
    } catch (error) {
      const errorMsg = error.message || '未知错误';
      if (errorMsg.includes('登录已过期')) {
        Toast.error(errorMsg);
      } else {
        Toast.error('提现失败: ' + errorMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="serve-page">
      <Header />
      <div className="serve-content">
        {/* Revenue Overview */}
        <section className="serve-stats-row">
          {stats.map((stat) => (
            <div key={stat.id} className="serve-stat-card">
              <div className={`serve-stat-icon ${stat.id === 'today' ? 'green' : 'blue'}`}>
                {stat.icon}
              </div>
              <div className="serve-stat-info">
                <span className="serve-stat-label">{stat.label}</span>
                <span className={`serve-stat-value ${stat.id === 'today' ? 'green' : ''}`}>
                  {stat.value}
                </span>
              </div>
            </div>
          ))}
        </section>

        {/* User's Revenue Section */}
        <section className="serve-user-card">
          <header className="serve-user-header">
            <div className="serve-section-title">
              <div className="serve-title-bar"></div>
              <h2>{t('serve.userRevenue')}</h2>
            </div>
            <button type="button" className="serve-join-button">{t('serve.joinVip')}</button>
          </header>

          <div className="serve-assets-grid">
            <div className="serve-asset-card">
              <div className="serve-asset-icon usdt">
                <SiTether />
              </div>
              <div className="serve-asset-name">USDT</div>
              <div className="serve-asset-value green">{userStats.usdtBalance}</div>
            </div>
            <div className="serve-asset-card">
              <div className="serve-asset-icon eth">
                <FaEthereum />
              </div>
              <div className="serve-asset-name">ETH</div>
              <div className="serve-asset-value yellow">{userStats.ethBalance}</div>
            </div>
          </div>

          <div className="serve-withdraw-card">
            <div className="serve-withdraw-info">
              <span className="serve-withdraw-label">{t('serve.withdrawableEarnings')}</span>
              <span className="serve-withdraw-value">{userStats.withdrawableEarnings}</span>
            </div>
            <button 
              type="button" 
              className="serve-draw-button"
              onClick={handleWithdraw}
              disabled={loading || !walletAddress || userStats.withdrawableEarnings <= 0}
            >
              {loading ? t('serve.processing') : t('serve.draw')}
            </button>
          </div>
        </section>

        {/* My Mining Assets */}
        {walletAddress && (
          <section className="serve-panel">
            <header className="serve-panel-header">
              <div className="serve-section-title">
                <div className="serve-title-bar"></div>
                <h3>{t('serve.myMiningAssets')}</h3>
              </div>
            </header>
            {myMiningAssets.length === 0 ? (
              <div className="serve-empty-state">
                <div className="serve-empty-illustration">⛏️</div>
                <p>{t('serve.noMiningAssets')}</p>
              </div>
            ) : (
              <div className="serve-assets-list">
                {myMiningAssets.map((asset, index) => (
                  <div key={index} className="serve-asset-row">
                    <div className="serve-asset-row-icon">
                      <AssetIcon assetName={asset.name} size={32} />
                    </div>
                    <div className="serve-asset-row-info">
                      <div className="serve-asset-row-name">{asset.name}</div>
                      <div className="serve-asset-row-quantity">{asset.quantity}</div>
                    </div>
                    <div className="serve-asset-row-value">
                      <div className="serve-asset-row-price green">${asset.price}</div>
                      <div className="serve-asset-row-total">${asset.totalValue}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        )}

        {/* Today's Mining Assets */}
        <section className="serve-panel">
          <header className="serve-panel-header">
            <div className="serve-section-title">
              <div className="serve-title-bar"></div>
              <h3>{t('serve.todayMiningAssets')}</h3>
            </div>
          </header>
          {miningAssets.length === 0 ? (
            <div className="serve-empty-state">
              <div className="serve-empty-illustration">💻</div>
              <p>{t('serve.noData')}</p>
            </div>
          ) : (
            <div className="serve-assets-list">
              {miningAssets.map((asset) => (
                <div key={asset.id || asset.assetId} className="serve-asset-row">
                  <div className="serve-asset-row-icon">
                    <AssetIcon assetName={asset.name} size={32} />
                  </div>
                  <div className="serve-asset-row-name">{asset.name}</div>
                  <div className="serve-asset-row-price green">${asset.price}</div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Purchase Product Record */}
        <section className="serve-panel">
          <header className="serve-panel-header">
            <div className="serve-section-title">
              <div className="serve-title-bar"></div>
              <h3>{t('serve.purchaseRecord')}</h3>
            </div>
          </header>
          {purchaseRecords.length === 0 ? (
            <div className="serve-empty-state">
              <div className="serve-empty-illustration mining">⛏️</div>
              <p>{t('serve.noData')}</p>
            </div>
          ) : (
            <div className="serve-table">
              {/* Purchase records table */}
            </div>
          )}
        </section>

        {/* Assembly Line */}
        <section className="serve-panel">
          <header className="serve-panel-header">
            <div className="serve-section-title">
              <div className="serve-title-bar"></div>
              <h3>{t('serve.assemblyLine')}</h3>
            </div>
          </header>
          {assemblyLineRecords.length === 0 ? (
            <div className="serve-empty-state">
              <div className="serve-empty-illustration">💻</div>
              <p>{t('serve.noData')}</p>
            </div>
          ) : (
            <div className="serve-table">
              <div className="serve-table-header">
                <div className="serve-table-cell serve-table-time">{t('serve.time')}</div>
                <div className="serve-table-cell serve-table-type">{t('serve.type')}</div>
                <div className="serve-table-cell serve-table-quantity">{t('serve.quantity')}</div>
              </div>
              <div className="serve-table-body">
                {assemblyLineRecords.map((record) => {
                  const date = new Date(record.createTime);
                  const timeStr = `${date.getFullYear()}/${String(date.getMonth() + 1).padStart(2, '0')}/${String(date.getDate()).padStart(2, '0')} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}:${String(date.getSeconds()).padStart(2, '0')}`;
                  const quantity = parseFloat(record.quantity || 0);
                  const quantityStr = quantity >= 0 
                    ? `${formatAsset(Math.abs(quantity))} ${record.assetType || ''}`.trim()
                    : `-${formatAsset(Math.abs(quantity))} ${record.assetType || ''}`.trim();
                  
                  return (
                    <div key={record.id} className="serve-table-row">
                      <div className="serve-table-cell serve-table-time">{timeStr}</div>
                      <div className="serve-table-cell serve-table-type">
                        <span className={`serve-type-badge serve-type-${record.type}`}>
                          {record.type || '-'}
                        </span>
                      </div>
                      <div className={`serve-table-cell serve-table-quantity ${quantity >= 0 ? 'serve-quantity-positive' : 'serve-quantity-negative'}`}>
                        {quantityStr}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </section>

        {/* Cooperation Platform */}
        <section className="serve-panel">
          <header className="serve-panel-header">
            <div className="serve-section-title">
              <div className="serve-title-bar"></div>
              <h3>{t('serve.cooperationPlatform')}</h3>
            </div>
          </header>
          <div className="serve-platforms-grid">
            {platforms.map((platform) => (
              <button key={platform.id} className="serve-platform-button">
                <span className="serve-platform-icon">{platform.icon}</span>
                <span className="serve-platform-name">{platform.name}</span>
              </button>
            ))}
          </div>
        </section>
      </div>
      <BottomNavigation activeTab="serve" onTabChange={onTabChange} />
    </div>
  );
};

export default Serve;
